/**
 * Feature flags the SPA needs at runtime.
 *
 * Fetched from `/api/me/features` rather than inferred client-side: the gate
 * is a server decision (an app default plus a per-user override), and the
 * client must not be able to grant itself a feature by editing local state.
 * The endpoint answers 200 with everything false when signed out, so an
 * anonymous visitor is a normal case rather than an error to handle.
 */
import { useEffect, useState } from 'react';
import type { FeaturesResponse } from '@ideabrief/core';

const NONE: FeaturesResponse = { feedbackEnabled: false };

/**
 * Resolves flags for the current session.
 *
 * Starts at "everything off" and only ever turns things ON once the server
 * answers, so a gated surface can never flash into view and disappear. Errors
 * leave it off — the same fail-closed posture the server takes.
 */
export function useFeatures(signedIn: boolean): FeaturesResponse {
  const [features, setFeatures] = useState<FeaturesResponse>(NONE);

  useEffect(() => {
    if (!signedIn) {
      setFeatures(NONE);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/me/features');
        if (!res.ok) return;
        const body = (await res.json()) as Partial<FeaturesResponse>;
        if (!cancelled) {
          // Demand a REAL boolean rather than a truthy value. `Boolean("no")`
          // and `Boolean("false")` are both true, so a coercion here would let
          // a malformed payload switch a gated feature on — the same trap
          // `hasAffirmativeConsent()` avoids on the server.
          setFeatures({ feedbackEnabled: body.feedbackEnabled === true });
        }
      } catch {
        // Leave flags off. A missing feature is invisible and self-corrects on
        // the next load; a wrongly-enabled one is not.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  return features;
}
